import React, { useEffect, useMemo, useState, useRef } from 'react';
import classNames from 'classnames';
import { concat, isEmpty, last } from 'lodash-es';
import { useMemoizedFn, useSize } from 'ahooks';
import { useIntl } from 'umi';

import { storage } from '@/utils/utils';
import { LANGUAGE_EN } from '@home/_config/config';
import QAInput, { createQAInputOnSubmit } from '@/pages/aigc/pages/Agent/components/QAInput';
import type { IHandleSubmitPayload } from '@/pages/aigc/pages/Agent/components/QAInput';
import type { IQAInputRef } from '@/pages/aigc/pages/Agent/components/QAInput/types';
import EllipsisHover from '@home/components/EllipsisHover';
import BasicImage from '@home/components/BasicImage';
import DsReportIconActive from '@/pages/aigc/assets/svg/data-science-report-icon-active.svg';
import AiTableIcon from '@/pages/aigc/assets/svg/agent-ai-excel.svg';
import MagicPosterIcon from '@/pages/aigc/assets/svg/agent-ai-image-generation.svg';
import { Conversation } from '@/pages/aigc/pages/Agent/ChatPage/types/history';
import { ChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import useChatHistory from '@/pages/aigc/pages/Agent/ChatPage/hooks/useChatHistory';
import { ChatTypes } from '../../ChatPage/types/chat';
import { useLocalState } from '../../ChatPage/hooks/useLocalState';
import { useSessionId } from '../../ChatPage/hooks/useAgentModalSata';
import { IReportData } from '../../components/ReportViewer/types';
import { useExternalQueryConfig } from '../hooks/useExternalQueryConfig';
import { ISelectedDsModelType } from '../../Home/components/DSAgent';
import { DSAgentModelResponse } from '../../service/service';
import { CopilotHomeRecommendation } from './CopilotHomeRecommendation';
import { CopilotDSHomeRecommendation } from './CopilotDSHomeRecommendation';
import { CopilotSingleQA } from './CopilotSingleQA';
import { CopilotAutoReleaseBar } from './CopilotScrollBar';
import CopilotAnswerLoading from './AnswerLoading';
import { CopilotChatTabs, CopilotTabType } from './CopilotChatTabs';
import { AiTableTabContent } from './AiTableTabContent';
import { MagicPosterTabContent } from './MagicPosterTabContent';
import {
  buildServerParams,
  buildFileListData,
  buildLogParams,
  filterConversationsByAction,
  getBuriedLogFrom,
  getEffectiveSkills,
  getQuotedContent,
} from '../../ChatPage/utils/submitHelpers';
import { resolveModelQueryForSubmit } from '@/pages/aigc/pages/Agent/components/QAInput/utils/inlineMentionLabel';
import { getExpectedTemplate, getTemplateFlags } from '@/pages/aigc/utils/utils';
import { useSkillsData } from '../../SkillsCollection/hooks';
import { IPosterTemplate } from '../../Home/components/PosterTemplateGallery/types';

interface IChatProps {
  changeSessionIdToOld: () => void;
  sessionId: string;
  voiceActiveBar: number;
  voiceIconWidth: number;
  dropAreaRef: React.MutableRefObject<HTMLDivElement | null> | undefined;
  fetchChatList: () => void;
  newSessionID: string;
  // TODO: chatList/bookMarkedChats 应使用 ChatList[] 类型，但 ChatList 接口缺少 agent_model/system_language/agent_banner 等后端实际返回的字段
  chatList?: ChatList[];
  bookMarkedChats?: ChatList[];
  /** 文件卡片点击回调 - 用于打开 DS 报告窗口 */
  onFileCardClick?: (dsReportData: IReportData) => void;
  /** DS Agent 专家列表数据（由父级请求） */
  dSExpertAgent?: DSAgentModelResponse['agent_models'][];
  /** DS Agent 数据加载状态 */
  dsAgentLoading?: boolean;
  /** 以下 props 用于 AiTableTabContent 直接渲染 ChatSmartTable */
  /** 报告数据（来自 useCopilotWindowManager） */
  dsReport?: { show?: boolean; [key: string]: unknown; };
  /** 报告会话 ID */
  reportSessionId?: string;
  /** 报告会话的 conversations */
  reportConversations?: unknown[];
  /** 流式状态 */
  streamingState?: string;
  /** 更新报告数据回调 */
  handleUpdateDsReport?: (update: { show?: boolean; [key: string]: unknown; }) => void;
  /** startNewConversation 的 ref（由 Copilot/index.tsx 始终传递） */
  startNewConversationRef: React.MutableRefObject<(...args: any[]) => void>;
  /** 打开报告窗口（用于自动打开新生成的表格和海报文件） */
  openReportWindow?: (reportData: IReportData) => void;
  /** 对话结束后刷新会话列表 + token 用量 */
  refreshAfterChat?: () => void;
}

/** startNewConversation 的回调选项 */
interface IStartConversationOptions {
  onOpen?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  onComplete?: () => void;
  onBefore?: (...args: unknown[]) => void;
}

/** startNewConversation 的额外参数 */
interface IStartConversationOtherParams {
  from?: 'edit' | 'refresh' | 'send' | 'recommend' | 'homeQuestion' | 'copilotRecommend';
  outConversationsList: Conversation[];
  logFrom?: string;
  serverParams?: Record<string, unknown>;
  customQuestion?: boolean;
  logParams?: Record<string, unknown>;
  fileListData?: unknown[];
  targetSessionId?: string;
  onInsertConversation?: (params: {
    conversation: Conversation;
    targetSessionId: string;
  }) => void;
}

// 暴露给父组件的方法接口
export interface ChatRef {
  handleChangeCopilotSession: (sessionId: string, cacheData?: Conversation[]) => void;
  handleUpdateSessionId: (sessionId?: string) => void;
  handleClearSessionId: () => void;
  conversations: Conversation[];
  sessionId: string;
  /** 开始新对话 - 用于报告窗口生成分析报告 */
  startNewConversation: (
    queryValue: string,
    singleMessageFileList: unknown,
    options?: IStartConversationOptions,
    otherParams?: IStartConversationOtherParams,
  ) => void;
  /** 流式状态 */
  streamingState: string;
}

/** 语音图标高度偏移量（px），用于内联样式计算 */
const VOICE_ICON_VERTICAL_OFFSET = 16;

/**
 * Copilot 聊天面板主组件
 * - 整合会话管理、消息发送/接收、语音输入、DS 报告、AI 表格、魔法海报等功能
 * - 对外暴露 ChatRef 接口供父组件控制会话切换、新建对话等操作
 */
// eslint-disable-next-line react/display-name
const Chat = React.forwardRef<ChatRef, IChatProps>(({
  changeSessionIdToOld,
  sessionId,
  voiceActiveBar,
  voiceIconWidth,
  dropAreaRef,
  fetchChatList,
  refreshAfterChat,
  newSessionID,
  chatList,
  bookMarkedChats,
  onFileCardClick,
  dSExpertAgent,
  dsAgentLoading,
  dsReport,
  reportSessionId,
  reportConversations,
  streamingState: streamingStateProp = '',
  handleUpdateDsReport,
  startNewConversationRef,
  openReportWindow,
}, ref) => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  // 是否是ds报告会话
  const [, setIsDSReportSession] = useLocalState('__dba_is_ds_report_session__', false);
  const [activeTab, setActiveTab] = useLocalState<CopilotTabType>('__dba_copilot_tab__', 'aiAgent');
  // DS Agent 选中模型状态
  const initSelectedSample = storage.getItem('__dba_ds_selected_sample') as ISelectedDsModelType;
  const [currentSelectedDsModel, setCurrentSelectedDsModel] = useState<ISelectedDsModelType | undefined>(initSelectedSample ?? undefined);

  // 选中的海报模板
  const [selectedPosterTemplate, setSelectedPosterTemplate] = useState<IPosterTemplate | 'prefix-only' | null>(null);

  const { sessionId: routeSessionId } = useSessionId();
  // Tab 切换回调
  const handleTabChangeCallback = useMemoizedFn((tabType: CopilotTabType) => {
    if (tabType !== activeTab) {
      setActiveTab(tabType);
      // 切换 tab 时重置魔法海报选中的模板
      setSelectedPosterTemplate(null);
    }
  });
  // 使用 useChatHistory hook 获取聊天相关的状态和方法
  const {
    conversations,
    streamingState,
    startNewConversation,
    cancelRequest,
    userInfo,
    errorTipsMessage,
    handleUpdateSessionId,
    handleChangeCopilotSession,
    handleClearSessionId,
    handleLikeOrDislike,
    fetchHistoryList,
    loading
  } = useChatHistory();

  // 获取用户安装的 Skills（用于 allowed_skills）
  const { allowedSkillsForApi, allSkills } = useSkillsData();
  
  // 自动打开新生成的表格和海报文件（Copilot 专用逻辑）
  const prevConversationRef = useRef<any[]>([]);
  const prevStreamingStateRef = useRef(streamingState);
  
  useEffect(() => {
    if (!openReportWindow || !conversations || conversations.length === 0) return;
    
    // 关键修复：只有在流式进行中或刚结束时，才自动打开文件
    // 避免历史对话加载时误触发自动打开
    const isCurrentlyStreaming = streamingState && ['pending', 'progressing'].includes(streamingState);
    const wasStreaming = prevStreamingStateRef.current && ['pending', 'progressing'].includes(prevStreamingStateRef.current as string);
    
    // 如果当前不是流式状态，且之前也不是流式状态，说明是历史数据加载，跳过自动打开
    if (!isCurrentlyStreaming && !wasStreaming) {
      prevConversationRef.current = conversations;
      prevStreamingStateRef.current = streamingState;
      return;
    }
    
    const lastConversation = conversations[conversations.length - 1];
    const currentAnswer = lastConversation?.answer || [];
    const prevConversation = prevConversationRef.current[prevConversationRef.current.length - 1];
    const prevAnswer = prevConversation?.answer || [];
    
    // 检测新添加的 document 类型项（ai_excel_file/magic_poster_file）
    // 方法：比较当前 answer 和之前的 answer
    if (currentAnswer.length > prevAnswer.length) {
      const newItems = currentAnswer.slice(prevAnswer.length);
      const docItem = newItems.find((item: any) => item.type === 'document');
      
      if (docItem) {
        const documentData = docItem.value;
        const fileType = docItem.subType || 'document'; // ai_excel_file, magic_poster_file, document
        
        // 调用 openReportWindow 打开文件
        // 注意：openReportWindow 使用 reportData.type 来确定类型，所以需要传递 type 字段
        openReportWindow({
          ...documentData,
          type: fileType, // 关键：使用 type 而不是 componentType
          componentType: fileType, // 同时传递 componentType 以兼容
          show: true,
          initialShow: true,
        } as any);
      }
    }
    
    // 如果流式响应刚结束，也检查一次（防止上述方法漏掉）
    const isStreamingFinished = wasStreaming && !streamingState;
    
    if (isStreamingFinished) {
      const docItems = currentAnswer.filter((item: any) => item.type === 'document');
      
      if (docItems.length > 0) {
        // 取最后一个（最新的）文件
        const lastDocItem = docItems[docItems.length - 1];
        const documentData = lastDocItem.value;
        const fileType = lastDocItem.subType || 'document';
        
        openReportWindow({
          ...documentData,
          type: fileType,
          componentType: fileType,
          show: true,
          initialShow: true,
        } as any);
      }
    }
    
    prevConversationRef.current = conversations;
    prevStreamingStateRef.current = streamingState;
  }, [conversations, streamingState, openReportWindow]);
  
  useEffect(() => {
    // 调整动机：这里原先如果是默认推荐页面的情况下（新对话），会默认发起get_history_list、以及v2接口，这里多余，直接屏蔽（会默认生成一个id，会导致触发get_history_list，返回空，跳转到落地页首页）
    // 现优化调整为，只有是一个旧对话，才会触发
    if (sessionId && !newSessionID) {
      fetchHistoryList();
    }
  }, [sessionId, newSessionID]);

  // 外部配置读取 & QAInput props 计算（读取后直接彻底清空 store，避免残留）
  const { externalQueryConfig, externalQueryInputProps } = useExternalQueryConfig({
    conversations,
    activeTab,
  });

  // 内部状态
  const [haveEdit, setHaveEdit] = React.useState(false);
  const qaInputRef = React.useRef<IQAInputRef>(null);
  const size = useSize(() => document.querySelector('.dba-copilot-chat-input'));
  const chatMenu = concat(chatList, bookMarkedChats)?.find((task) => task?.session_id === routeSessionId);
  const selectedSample = storage.getItem('__dba_ds_selected_sample') as ISelectedDsModelType;
  const isProcessing = ['pending', 'progressing'].includes(streamingState);

  useEffect(() => {
    if (chatMenu?.agent_type === "ds_agent") {
      setIsDSReportSession(true);
      return;
    }
    !isProcessing && setIsDSReportSession(chatMenu?.agent_type === "ds_agent");
  }, [isProcessing,chatMenu]);

  // 全局会话模式状态：标识当前会话属于什么模式，供 QAInput / AgentModeSelector / handleSubmit 等消费
  const [sessionTemplateMode, setSessionTemplateMode] = useLocalState<string>('__dba_session_template_mode__', '');

  /**
   * 会话模式同步：
   * - chatMenu 存在：按 agent_type 推断（普通会话写 ''、skill 会话写 'skill'）
   * - chatMenu 不存在（新会话 / chatList 未回流）：按 activeTab 默认
   * - 依赖收敛到字符串 agent_type 而非 chatMenu 引用，避免 fetchChatList 引起的引用变化
   *   导致重复写入，覆盖 QAInput 发送瞬间写入的 'skill'
   */
  useEffect(() => {
    // 新会话或空会话
    if (!sessionId || newSessionID === sessionId) {
      // 根据当前 activeTab 设置 sessionTemplateMode，确保 AgentModeSelector 中的
      // visibleScenes 过滤逻辑在 Copilot 各 Tab 下与 ChatPage 保持一致
      // aiAgent 是默认模式，设置为空字符串以显示所有模型（与 ChatPage 默认行为一致）
      setSessionTemplateMode(activeTab === 'aiAgent' ? '' : activeTab);
      return;
    }
    // chatList 未加载完成时，等待加载
    if (!chatList && !bookMarkedChats) return;
    // 历史会话：根据 chatMenu 使用 getExpectedTemplate 推断模式
    setSessionTemplateMode(getExpectedTemplate(chatMenu));
  }, [sessionId, newSessionID, chatMenu, chatList, bookMarkedChats, activeTab]);

  // 页面离开时清空全局会话模式状态，避免污染其他页面
  useEffect(() => {
    return () => {
      setSessionTemplateMode('');
    };
  }, []);

  const isDsReportChat = ((chatMenu?.agent_type === "ds_agent") || (activeTab === 'dsReport' && isEmpty(conversations)));
  const isAiTableChat = activeTab === 'aiExcel' && isEmpty(conversations);
  const isMagicPosterChat = activeTab === 'magicPoster' && isEmpty(conversations);

  /**
   * 处理消息提交
   * @param payload - 提交对象入参（文案、文件、引用标签、来源等）
   */
  const handleSubmit = useMemoizedFn(
    ({
      value,
      fileList: singleMessageFileList,
      options,
      from,
      deleteMessageIds,
      serverProps,
      mentionConfig,
      logParams: submitLogParams,
    }: IHandleSubmitPayload) => {
      const { selectedSkills, selectedCharts, selectedKnowledge } = mentionConfig ?? {};
      changeSessionIdToOld();

      // 1. 获取 DS 报告模式的配置信息
      const dsReportChatConfig = isDsReportChat
        ? chatList?.find((task) => task?.session_id === sessionId) || { agent_model: '', system_language: '' }
        : null;

      // 1.5 Skill 会话中，根据 from 决定 skill 的生效策略（edit/refresh/其他）
      const { isSkill: isSkillSession } = getTemplateFlags(undefined, sessionTemplateMode);
      const effectiveIsSkill =
        isSkillSession || !isEmpty(selectedSkills) || !isEmpty(selectedCharts);
      const { skills: effectiveSelectedSkills, isInherited: isSkillInherited } = getEffectiveSkills({
        selectedSkills,
        isSkillSession: effectiveIsSkill,
        conversations,
        from,
        frontendMetadata: serverProps?.frontend_metadata,
      });

      // 1.6 知识库：展示 query（折叠）vs 模型 query（≥3 平铺）
      const displayQuery = value;
      const kbDocUnit = formatMessage({ id: 'dba_kb_inline_doc_unit' });
      const modelQuery = resolveModelQueryForSubmit(
        displayQuery,
        selectedKnowledge,
        kbDocUnit,
        serverProps?.frontend_metadata?.kb_expand_token as
          | { from: string; to: string; }
          | undefined,
      );
      // 1.7 解析 enrichedExternalQueryConfig（注入 quoted_content，参照 getEffectiveSkills 继承模式）
      const enrichedExternalQueryConfig = getQuotedContent(conversations, externalQueryConfig, from);

      // 2. 构建服务器参数
      const serverParams = buildServerParams({
        userId: userInfo?.openid,
        modelId: serverProps?.model_id,
        mode: serverProps?.mode,
        externalQueryConfig: enrichedExternalQueryConfig,
        dsReportConfig: {
          isDsMode: isDsReportChat,
          agentModel: dsReportChatConfig?.agent_model,
          systemLanguage: dsReportChatConfig?.system_language,
          initialModelId: selectedSample?.modelId,
          locale: selectedSample?.locale,
        },
        aiExcelConfig: {
          isExcel: isAiTableChat,
        },
        magicPosterConfig: {
          isMagicPoster: isMagicPosterChat,
        },
        skillConfig: {
          isSkill: effectiveIsSkill,
          selectedSkills: effectiveSelectedSkills,
          installedSkills: allowedSkillsForApi,
          allSkills,
          isInherited: isSkillInherited,
        },
        fileList: singleMessageFileList,
        serverProps: serverProps || qaServerParams,
        selectedCharts,
        selectedKnowledge,
        displayQuery,
        modelQuery,
        platform: 'copilot',
      });

      // 3. 构建日志参数
      const logParams = buildLogParams({
        dataSource: 'copilot',
        externalQueryConfig,
        selectedSkills: effectiveSelectedSkills,
        fileList: singleMessageFileList,
        selectedCharts,
        selectedKnowledge,
        kbDocUnit,
        submitLogParams,
      });

      // 4. 过滤对话列表（编辑/刷新操作）
      const outConversationsList = filterConversationsByAction({
        from,
        deleteMessageIds,
        conversations,
      });

      // 5. 构建文件数据：serverParts 给后端（含外部 files），frontFileList 给本地 conversation
      //    编辑/刷新时通过 editContext 回灌原始 parts，buildFileListData 内部处理
      const { serverParts: fileListData, frontFileList } = buildFileListData(
        singleMessageFileList,
        selectedCharts,
        enrichedExternalQueryConfig?.files,
        { from, deleteMessageIds, conversations },
      );

      // 6. 更新 DS 报告会话状态
      setIsDSReportSession(isDsReportChat);
      // 7. 发起新对话
      startNewConversation(
        modelQuery,
        frontFileList,
        {
          ...options,
          onOpen: () => {
            fetchChatList();
          },
          onComplete: () => {
            // fetchChatList();
            refreshAfterChat?.();
            options?.onComplete?.();
          },
          onError: () => {
            options?.onError?.();
          },
        },
        {
          from,
          outConversationsList,
          logFrom: getBuriedLogFrom(from),
          logParams,
          fileListData,
          serverParams,
        },
      );
    },
  );

  /** Copilot 空会话推荐点击：走 QAInput 提交流程，携带当前 Skill / Chart */
  const handleCopilotHomeRecommendClick = useMemoizedFn((value: string): void => {
    qaInputRef.current?.submitText(value, { from: 'homeQuestion' });
  });

  /** Copilot 猜你想问 / 快捷输入按钮点击 */
  const handleCopilotRecommendClick = useMemoizedFn((value: string, isWebSearch?: boolean): void => {
    qaInputRef.current?.submitText(value, {
      from: 'copilotRecommend',
      serverProps: { use_websearch: isWebSearch },
    });
  });

  /** QAInput 手动发送回调，适配为 Copilot handleSubmit */
  const handleQAInputSubmit = useMemo(
    () => createQAInputOnSubmit(handleSubmit),
    [handleSubmit],
  );

  // AI Agent Tab 内容
  const aiAgentContent = (
    <CopilotHomeRecommendation
      handleClickRecommend={handleCopilotHomeRecommendClick}
    />
  );

  // DS Agent Tab 内容
  const dsAgentContent = (
    <CopilotDSHomeRecommendation
      currentSelectedDsModel={currentSelectedDsModel}
      setCurrentSelectedDsModel={setCurrentSelectedDsModel}
      dSExpertAgent={dSExpertAgent}
      loading={dsAgentLoading}
      inputAreaHeight={size?.height}
    />
  );

  // AI 表格 Tab 内容（已抽离为 AiTableTabContent 组件）
  // 当有 dsReport 数据时，AiTableTabContent 会直接渲染 ChatSmartTable
  // 注意：仅当前会话与报告归属会话一致时，聊天区才展示报告表格，
  // 否则（如新建对话）显示推荐问题，确保弹窗（CopilotReportWindow）保留但聊天区不残留表格
  const aiTableDsReport = useMemo(() => {
    if (reportSessionId && reportSessionId !== sessionId) return { show: false };
    return dsReport;
  }, [dsReport, reportSessionId, sessionId]);
  const aiTableContent = (
    <AiTableTabContent
      onClickRecommend={(value) => handleSubmit({
        value,
        fileList: [],
        options: {},
        from: 'homeQuestion',
      })}
      dsReport={aiTableDsReport}
      sessionId={reportSessionId || sessionId}
      handleUpdateDsReport={handleUpdateDsReport}
      startNewConversation={startNewConversationRef.current}
      conversations={reportConversations}
      streamingState={streamingStateProp}
      readonly={false}
      onFocus={() => {}}
      onHeaderMouseDown={() => {}}
    />
  );

  // 数据魔法海报 Tab 内容（已抽离为 MagicPosterTabContent 组件）
  const magicPosterContent = (
    <MagicPosterTabContent
      onSelectTemplate={setSelectedPosterTemplate}
      inputAreaHeight={size?.height}
    />
  );

  // 魔法海报 taggingNode
  const posterTaggingNode = useMemo(() => {
    if (activeTab !== 'magicPoster' || !selectedPosterTemplate) return undefined;
    if (selectedPosterTemplate === 'prefix-only') {
      return (
        <span key="prefix-only" className='dba-poster-tag-wrapper'>
          <span className="dba-poster-tag-prefix">{isEn ? 'Based on' : '以'}</span>
        </span>
      );
    }
    return (
      <span key={selectedPosterTemplate.templateId} className='dba-poster-tag-wrapper'>
        <span className="dba-poster-tag-prefix">{isEn ? 'Based on' : '以'}</span>
        <span className="dba-poster-tag">
          <BasicImage src={(isEn ? selectedPosterTemplate.coverUrl_en : selectedPosterTemplate.coverUrl_cn) || ''} alt={isEn ? selectedPosterTemplate.titleEn : selectedPosterTemplate.titleCn} className="dba-poster-tag-icon" />
          {isEn ? selectedPosterTemplate.titleEn : selectedPosterTemplate.titleCn}
        </span>
      </span>
    );
  }, [activeTab, selectedPosterTemplate, isEn]);

  // 实际模板对象（排除 'prefix-only'）
  const actualPosterTemplate = selectedPosterTemplate && selectedPosterTemplate !== 'prefix-only' ? selectedPosterTemplate : null;

  // 根据当前 tab 构建 QAInput 的 serverParams
  const qaServerParams = useMemo(() => {
    const functionNameMap: Record<string, string> = {
      aiExcel: 'ai_excel',
      magicPoster: 'magic_poster',
    };
    const fnName = functionNameMap[activeTab];
    if (!fnName) return undefined;
    // 修复：深合并 frontend_metadata，避免 externalQueryConfig 覆盖 magic poster / ai excel 的 frontend_metadata
    const posterFm = activeTab === 'magicPoster' ? {
      type: 'magic_poster' as const,
      poster_template: actualPosterTemplate ? 1 : 0,
      ...(actualPosterTemplate ? { templateId: actualPosterTemplate.templateId } : {}),
    } : undefined;
    const externalFm: Record<string, unknown> | undefined = externalQueryConfig ? {
      field_name: externalQueryConfig.prefixTag,
      type: externalQueryConfig.form,
    } : undefined;
    return {
      function: {
        name: fnName,
        source: 'main_chat',
        ...(activeTab === 'magicPoster' ? {
          args: {
            content_prompt: isEn ? actualPosterTemplate?.content_prompt_en : actualPosterTemplate?.content_prompt_cn,
            poster_prompt: isEn ? actualPosterTemplate?.poster_prompt_en : actualPosterTemplate?.poster_prompt_cn,
            poster_template: actualPosterTemplate ? 1 : 0,
          },
        } : {}),
      },
      ...((posterFm || externalFm) ? {
        frontend_metadata: {
          ...posterFm,
          ...externalFm,
        },
      } : {}),
    };
  }, [activeTab, actualPosterTemplate, isEn, externalQueryConfig]);

  // 使用 useImperativeHandle 暴露方法给父组件
  React.useImperativeHandle(ref, () => ({
    handleChangeCopilotSession,
    handleUpdateSessionId,
    handleClearSessionId,
    conversations,
    sessionId,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- startNewConversation is async, wrapped to satisfy ChatRef void contract
    startNewConversation: (...args) => { void (startNewConversation(...args as [string, unknown, ...any[]])) },
    streamingState,
  }), [handleChangeCopilotSession, handleUpdateSessionId, handleClearSessionId, conversations, sessionId, startNewConversation, streamingState]);

  // 获取报表模板
  const dsReportName = useMemo(() => {
    if (!isDsReportChat) return {};
    let data = {};
    const chatMenuList = concat(chatList, bookMarkedChats)?.find((task) => task?.session_id === sessionId);
    if (!isEmpty(chatMenuList)) {
      data = chatMenuList;
      data.agent_title = dSExpertAgent?.find((item) => item.model_id === chatMenuList?.agent_model)?.title;
    } else {
      if (!isEmpty(selectedSample)) {
        data = {
          agent_title: selectedSample.name,
          agent_banner: chatMenuList?.agent_banner
        };
      }
    }
    return data;
  }, [chatList, sessionId, bookMarkedChats, dSExpertAgent, selectedSample, isDsReportChat]);

  return (
    <>
      {/* Tab 切换区域 - 使用 CopilotChatTabs 组件 */}
      {isEmpty(conversations) && !loading && <CopilotChatTabs
        defaultActiveId={activeTab}
        onTabChangeCallback={handleTabChangeCallback}
        aiAgentContent={aiAgentContent}
        dsAgentContent={dsAgentContent}
        aiTableContent={aiTableContent}
        magicPosterContent={magicPosterContent}
        showContent={isEmpty(conversations)}
      />}

      {loading ? (
        <CopilotAnswerLoading className='dba-copilot-answer-loading-container' />
      ) : (
        <>
          {/* TODO: 这个组件待迁移，状态从内部上升，父级组件state统一干掉 */}
          <CopilotSingleQA
            {...{
              conversations,
              streamingState,
              userInfo,
              errorTipsMessage,
              isProcessing,
              handleSubmit,
              setHaveEdit,
              voiceActiveBar,
              style: { '--voice-icon-width': `${voiceIconWidth ?? 0}px` },
              handleLikeOrDislike,
              dsReportName,
              onFileCardClick,
              onRecommendClick: handleCopilotRecommendClick,
            }}
          />
          {/* 自动滚动 */}
          <CopilotAutoReleaseBar
            key={sessionId}
            {...{
              streamingState,
              style: { bottom: (size?.height ?? 0) + (isDsReportChat ? 60 : 30) },
            }}
          />
        </>
      )}

      {/* 底部输入框 */}
      <QAInput
        ref={qaInputRef}
        {...{
          state: streamingState,
          onSubmit: handleQAInputSubmit,
          key: `${sessionId}-${activeTab}`,
          ...externalQueryInputProps,
          externalQueryConfig,
          startupPrompt: !loading && isDsReportChat && (isEmpty(conversations) || ((isEmpty(dsReportName)))),
          taggingNode: isDsReportChat || loading ? <div style={{ display: 'none' }} /> : posterTaggingNode,
          currentSelectedDsModel,
          disabled: !currentSelectedDsModel && activeTab === 'dsReport',
          lastMessages: last(conversations),
          isProcessing,
          cancelRequest,
          from: 'copilot',
          className: classNames('dba-copilot-chat-input', {
            'dba-home-input-ds-agent': activeTab === ChatTypes.DS_AGENT_CHAT || activeTab === 'magicPoster',
          }),
          isCopilot: true, // 标记为 Copilot 输入框
          dropAreaRef,
          sessionId,
          voiceActiveBar,
          style: { '--voice-icon-width': `${voiceIconWidth ?? 0}px` },
          isPosterMode: activeTab === 'magicPoster',
          selectedPosterTemplate: actualPosterTemplate,
          serverParams: qaServerParams,
          onTaggingNodeClear: () => {
            if (activeTab === 'magicPoster') {
              // 魔法海报：两步删除 → 有模板→仅前缀→null
              if (selectedPosterTemplate && selectedPosterTemplate !== 'prefix-only') {
                setSelectedPosterTemplate('prefix-only');
              } else {
                setSelectedPosterTemplate(null);
              }
            }
          },
          customHeader: (() => {
            // AI表格标签框
            // 必须同时检查 isEmpty(conversations) 或 chatMenu.function.name，防止切换历史会话后 activeTab 残留导致错误显示
            if (activeTab === 'aiExcel' && !loading && (isEmpty(conversations) || (chatMenu as any)?.function?.name === 'ai_excel')) {
              return (
                <div
                  style={{ '--voice-icon-height': `${(size?.height ?? 0) + VOICE_ICON_VERTICAL_OFFSET}px` }}
                  className={classNames('dba-qa-input-header-template', 'dba-qa-input-header')}
                >
                  <div className='dba-qa-input-header-text'>
                    <EllipsisHover
                      text={<>
                        <img src={AiTableIcon} alt="" />
                        {formatMessage({ id: 'dba_chat_type_ai_table' })}
                      </>}
                    />
                  </div>
                </div>
              );
            }
            // 数据魔法海报标签框
            // 必须同时检查 isEmpty(conversations) 或 chatMenu.function.name，防止切换历史会话后 activeTab 残留导致错误显示
            if (activeTab === 'magicPoster' && !loading && (isEmpty(conversations) || (chatMenu as any)?.function?.name === 'magic_poster')) {
              return (
                <div
                  style={{ '--voice-icon-height': `${(size?.height ?? 0) + VOICE_ICON_VERTICAL_OFFSET}px` }}
                  className={classNames('dba-qa-input-header-template', 'dba-qa-input-header')}
                >
                  <div className='dba-qa-input-header-text'>
                    <EllipsisHover
                      text={<>
                        <img src={MagicPosterIcon} alt="" />
                        {formatMessage({ id: 'dba_chat_type_magic_poster' })}
                      </>}
                    />
                  </div>
                </div>
              );
            }
            // DS报告标签框（原有逻辑）
            if (dsReportName?.agent_title && !loading) {
              return (
                <div
                  style={{ '--voice-icon-height': `${(size?.height ?? 0) + VOICE_ICON_VERTICAL_OFFSET}px` }}
                  className={classNames('dba-qa-input-header-template', 'dba-qa-input-header')}
                >
                  <div className='dba-qa-input-header-text'>
                    <EllipsisHover
                      text={<>
                        <img src={DsReportIconActive} alt="" />
                        {formatMessage({ id: 'dba_ds_report' })}【{dsReportName?.agent_title?.trim()}】
                      </>}
                    />
                  </div>
                </div>
              );
            }
            return undefined;
          })(),
        }}
        disableDragUpload={haveEdit}
      />
    </>
  );
});

export default Chat;
